
import { useEffect, useMemo, useRef } from "react";
import { PRICE_LIKE_COLUMNS, SKU_LIKE_COLUMNS } from "@/lib/mapper/columns";
import type {
  VariantConfig,
  VariantDimension,
  VariantPriceAdj,
  VariantValue,
} from "@/lib/mapper/types";
import { normalizeVariants } from "@/lib/mapper/variants";

const MAX_DIMENSIONS = 3;
const DEFAULT_APPLY_TO = [...PRICE_LIKE_COLUMNS, ...SKU_LIKE_COLUMNS];

type Props = {
  variants: VariantConfig | undefined;
  onChange: (next: VariantConfig) => void;
  onClose: () => void;
  applyToOptions?: readonly string[];
};

function sanitizeValue(value: VariantValue): VariantValue {
  return {
    value: typeof value.value === "string" ? value.value : "",
    priceAdj: {
      mode: value.priceAdj?.mode === "percent" ? "percent" : "fixed",
      amount:
        typeof value.priceAdj?.amount === "number" &&
        Number.isFinite(value.priceAdj.amount)
          ? value.priceAdj.amount
          : 0,
    },
    skuSuffix: typeof value.skuSuffix === "string" ? value.skuSuffix : "",
  };
}

function sanitizeDimensions(
  variants: VariantConfig | undefined,
): VariantDimension[] {
  const normalized = normalizeVariants(variants)
    .slice(0, MAX_DIMENSIONS)
    .map((dimension, index) => ({
      name:
        typeof dimension.name === "string" && dimension.name.trim().length > 0
          ? dimension.name
          : `Option ${index + 1}`,
      values: Array.isArray(dimension.values)
        ? dimension.values.map(sanitizeValue)
        : [],
    }));

  if (normalized.length > 0) return normalized;
  return [{ name: variants?.optionName || "Stitching", values: [] }];
}

function applyToColumns(variants: VariantConfig | undefined, columns: readonly string[] = DEFAULT_APPLY_TO): string[] {
  const configured = variants?.applyTo?.filter((column) => column.length > 0);
  return configured && configured.length > 0 ? configured : [...columns];
}

function nextConfig(
  dimensions: VariantDimension[],
  applyTo: string[],
): VariantConfig {
  return {
    enabled: true,
    dimensions: dimensions.slice(0, MAX_DIMENSIONS),
    applyTo,
  };
}

export function LayeredVariantsPanel({ variants, onChange, onClose, applyToOptions }: Props) {
  const initialized = useRef(false);
  const options = applyToOptions ?? DEFAULT_APPLY_TO;
  const dimensions = useMemo(() => sanitizeDimensions(variants), [variants]);
  const applyTo = useMemo(() => applyToColumns(variants, options), [variants, options]);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    onChange(nextConfig(dimensions, applyTo));
  }, [applyTo, dimensions, onChange]);

  function commit(nextDimensions: VariantDimension[], nextApplyTo = applyTo) {
    onChange(nextConfig(nextDimensions, nextApplyTo));
  }

  function updateDimension(index: number, patch: Partial<VariantDimension>) {
    commit(
      dimensions.map((dimension, i) =>
        i === index ? { ...dimension, ...patch } : dimension,
      ),
    );
  }

  function updateValue(
    dimensionIndex: number,
    valueIndex: number,
    patch: Partial<VariantValue>,
  ) {
    const nextDimensions = dimensions.map((dimension, i) => {
      if (i !== dimensionIndex) return dimension;
      return {
        ...dimension,
        values: dimension.values.map((value, j) =>
          j === valueIndex ? sanitizeValue({ ...value, ...patch }) : value,
        ),
      };
    });
    commit(nextDimensions);
  }

  function updatePriceAdj(
    dimensionIndex: number,
    valueIndex: number,
    patch: Partial<VariantPriceAdj>,
  ) {
    const current =
      dimensions[dimensionIndex]?.values[valueIndex]?.priceAdj ?? {
        mode: "fixed",
        amount: 0,
      };
    updateValue(dimensionIndex, valueIndex, {
      priceAdj: {
        mode: patch.mode ?? current.mode,
        amount: patch.amount ?? current.amount,
      },
    });
  }

  function addDimension() {
    if (dimensions.length >= MAX_DIMENSIONS) return;
    commit([
      ...dimensions,
      { name: `Option ${dimensions.length + 1}`, values: [] },
    ]);
  }

  function removeDimension(index: number) {
    const next = dimensions.filter((_, i) => i !== index);
    commit(next.length > 0 ? next : [{ name: "Stitching", values: [] }]);
  }

  function addValue(dimensionIndex: number) {
    updateDimension(dimensionIndex, {
      values: [
        ...dimensions[dimensionIndex].values,
        { value: "", priceAdj: { mode: "fixed", amount: 0 }, skuSuffix: "" },
      ],
    });
  }

  function removeValue(dimensionIndex: number, valueIndex: number) {
    updateDimension(dimensionIndex, {
      values: dimensions[dimensionIndex].values.filter(
        (_, i) => i !== valueIndex,
      ),
    });
  }

  function toggleApplyTo(column: string) {
    const next = applyTo.includes(column)
      ? applyTo.filter((item) => item !== column)
      : [...applyTo, column];
    commit(dimensions, next);
  }

  return (
    <section className="shrink-0 border-b border-slate-200 dark:border-border bg-slate-50 dark:bg-background px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Variants
          </div>
          <p className="mt-1 text-[12px] text-slate-500 dark:text-slate-400">
            Build up to three Shopify option dimensions with price and SKU adjustments.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-2 py-1 text-[11px] text-slate-600 dark:text-slate-400 transition hover:bg-slate-100 dark:hover:bg-surface-2 hover:text-slate-900 dark:hover:text-slate-100"
        >
          Close
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {options.map((column) => (
          <label
            key={column}
            className="inline-flex items-center gap-1.5 rounded border border-slate-200 dark:border-border bg-white dark:bg-surface px-2 py-1 text-[11px] text-slate-700 dark:text-slate-300"
          >
            <input
              type="checkbox"
              checked={applyTo.includes(column)}
              onChange={() => toggleApplyTo(column)}
            />
            {column}
          </label>
        ))}
      </div>

      <div className="mt-3 space-y-3">
        {dimensions.map((dimension, dimensionIndex) => (
          <div
            key={dimensionIndex}
            className="rounded border border-slate-200 dark:border-border bg-white dark:bg-surface"
          >
            <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 dark:border-border px-3 py-2">
              <label className="block min-w-56 flex-1 text-[11px] text-slate-600 dark:text-slate-400">
                Dimension {dimensionIndex + 1} name
                <input
                  type="text"
                  value={dimension.name}
                  onChange={(event) =>
                    updateDimension(dimensionIndex, {
                      name: event.target.value,
                    })
                  }
                  placeholder="Size"
                  className="mt-1 w-full rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-2 py-1 text-[12px] text-slate-950 dark:text-slate-100 focus:border-slate-500 dark:focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 dark:focus:ring-slate-600"
                />
              </label>
              <button
                type="button"
                onClick={() => removeDimension(dimensionIndex)}
                disabled={dimensions.length === 1}
                className="rounded border border-slate-200 dark:border-border px-2 py-1 text-[11px] text-slate-500 dark:text-slate-400 transition hover:border-red-300 dark:hover:border-red-800 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-600 dark:hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Remove dimension
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-[720px] w-full text-[12px]">
                <thead className="bg-slate-50 dark:bg-background text-[10px] uppercase text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold">Value</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Price mode</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Signed amount</th>
                    <th className="px-2 py-1.5 text-left font-semibold">SKU suffix</th>
                    <th className="px-1 py-1.5" />
                  </tr>
                </thead>
                <tbody>
                  {dimension.values.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-2 py-3 text-center text-[11px] text-slate-400 dark:text-slate-500"
                      >
                        No values yet.
                      </td>
                    </tr>
                  ) : (
                    dimension.values.map((value, valueIndex) => (
                      <tr
                        key={valueIndex}
                        className="border-t border-slate-100 dark:border-border"
                      >
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            value={value.value}
                            onChange={(event) =>
                              updateValue(dimensionIndex, valueIndex, {
                                value: event.target.value,
                              })
                            }
                            placeholder="Stitched"
                            className="w-full rounded border border-slate-200 dark:border-border bg-white dark:bg-surface px-2 py-1 text-[12px] text-slate-950 dark:text-slate-100"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <select
                            value={value.priceAdj?.mode ?? "fixed"}
                            onChange={(event) =>
                              updatePriceAdj(dimensionIndex, valueIndex, {
                                mode:
                                  event.target.value === "percent"
                                    ? "percent"
                                    : "fixed",
                              })
                            }
                            className="w-full rounded border border-slate-200 dark:border-border bg-white dark:bg-surface px-2 py-1 text-[12px] text-slate-950 dark:text-slate-100"
                          >
                            <option value="fixed">Fixed</option>
                            <option value="percent">Percent</option>
                          </select>
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            value={value.priceAdj?.amount ?? 0}
                            onChange={(event) =>
                              updatePriceAdj(dimensionIndex, valueIndex, {
                                amount: Number(event.target.value) || 0,
                              })
                            }
                            className="w-full rounded border border-slate-200 dark:border-border bg-white dark:bg-surface px-2 py-1 text-right text-[12px] tabular-nums text-slate-950 dark:text-slate-100"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            value={value.skuSuffix ?? ""}
                            onChange={(event) =>
                              updateValue(dimensionIndex, valueIndex, {
                                skuSuffix: event.target.value,
                              })
                            }
                            placeholder="-ST"
                            className="w-full rounded border border-slate-200 dark:border-border bg-white dark:bg-surface px-2 py-1 font-mono text-[12px] text-slate-950 dark:text-slate-100"
                          />
                        </td>
                        <td className="px-1 py-1 text-right">
                          <button
                            type="button"
                            onClick={() =>
                              removeValue(dimensionIndex, valueIndex)
                            }
                            className="rounded px-2 py-1 text-slate-400 dark:text-slate-500 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-600 dark:hover:text-red-300"
                            aria-label="Remove variant value"
                          >
                            x
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-t border-slate-100 dark:border-border px-3 py-2">
              <button
                type="button"
                onClick={() => addValue(dimensionIndex)}
                className="rounded border border-dashed border-slate-300 dark:border-border px-2 py-1 text-[11px] text-slate-600 dark:text-slate-400 transition hover:border-slate-900 dark:hover:border-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
              >
                + Add value
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3">
        <button
          type="button"
          onClick={addDimension}
          disabled={dimensions.length >= MAX_DIMENSIONS}
          className="rounded border border-dashed border-slate-300 dark:border-border bg-white dark:bg-surface px-3 py-1.5 text-[12px] font-medium text-slate-700 dark:text-slate-300 transition hover:border-slate-900 dark:hover:border-slate-400 hover:text-slate-900 dark:hover:text-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          + Add dimension
        </button>
      </div>
    </section>
  );
}
